<template>
  <li
    :id="reviewerId"
    :class="['flex flex-col sm:flex-row sm:items-start bg-faint-grey rounded-lg p-4', { 'drag-handle cursor-move': isDraggable }]">
    <div class="flex flex-1 items-top">
      <DragHandleIcon
        v-if="isDraggable"
        :class="['mr-2 text-royal-blue-160 shrink-0']"
        :is-decorative="true"
        :data-testid="`drag-icon-reviewer-${props.reviewer.type}-${pos}`"/>
      <span
        class="w-6 h-6 flex-shrink-0 flex items-center justify-center border-royal-blue-160 border-2 bg-white rounded-full text-base font-bold text-center mr-4"
        aria-hidden="true">
        {{ pos }}
      </span>
      <div class="flex flex-col flex-1">
        <div class="flex flex-col sm:flex-row sm:justify-between sm:items-start">
          <div class="break-all">
            <p
              :id="nameId"
              class="font-bold mb-1 data-hj-suppress"
              data-testid="reviewer-name">
              {{ fullName }}
            </p>
            <p
              :id="emailId"
              class="data-hj-suppress"
              data-testid="reviewer-email">
              {{ props.reviewer.email }}
            </p>
            <p
              v-if="showReason"
              :id="reasonId"
              class="data-hj-suppress mt-1"
              data-testid="reviewer-reason">
              {{ props.reviewer.reason }}
            </p>
          </div>
          <ButtonComponent
            v-if="showEdit"
            class="w-fit mt-4 sm:mt-0 sm:ml-4"
            label="Edit"
            variant="secondary"
            :id="editButtonId"
            data-testid="reviewer-card-btn"
            @customClick="edit"
          />
        </div>
      </div>
    </div>
  </li>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Reviewer } from '@/types'
import ButtonComponent from '../ui-components/ButtonComponent.vue'
import DragHandleIcon from '../ui-components/icons/DragHandleIcon.vue'
import { useReviewerManagement } from '@/composables/useReviewerManagement'

const emit = defineEmits(['edit'])

const props = defineProps({
  reviewer: { required: true, type: Object as () => Reviewer },
  showEdit: { required: false, type: Boolean, default: true },
  showReason: { required: false, type: Boolean, default: false },
  isDraggable: { required: false, type: Boolean, default: false },
})

const { generateReviewerId, getReviewerFullName, getReviewerPosition } = useReviewerManagement()

const pos = computed(() => getReviewerPosition(props.reviewer.id, props.reviewer.type))
const reviewerId = computed(() => generateReviewerId(props.reviewer.type, pos.value, 'ListItem'))
const fullName = computed(() => getReviewerFullName(props.reviewer))
const nameId = computed(() => generateReviewerId(props.reviewer.type, pos.value, 'Name'))
const emailId = computed(() => generateReviewerId(props.reviewer.type, pos.value, 'Email'))
const reasonId = computed(() => generateReviewerId(props.reviewer.type, pos.value, 'Reason'))
const editButtonId = computed(() => generateReviewerId(props.reviewer.type, pos.value, 'ItemEditButton'))

const edit = () => emit('edit', props.reviewer)
</script>
